import { useEffect, useRef } from "react";
import { oklchToHex } from "@/document/model/color";
import { colorSamplerLabel, type ColorSamplePreview, type ColorSamplerRequest } from "./colorSampler";
import type { FrameLabelOverlay } from "./canvasWorkspaceTypes";

export function ColorSampleLoupe({ preview, mode }: { preview: ColorSamplePreview; mode: ColorSamplerRequest["mode"] }) {
  const minWidth = 176;
  const maxWidth = Math.max(minWidth, window.innerWidth - 16);
  const height = 56;
  const left = Math.min(window.innerWidth - minWidth - 8, preview.x + 18);
  const top = Math.min(window.innerHeight - height - 8, preview.y + 18);
  const label = colorSamplerLabel(preview.color, mode);
  const swatch = oklchToHex(preview.color);

  return (
    <div
      className="pointer-events-none fixed z-50 flex items-center gap-2 rounded-md border border-white/15 bg-[#20252b] p-1.5 text-[11px] text-slate-200 shadow-2xl shadow-black/45"
      style={{ left: Math.max(8, left), top: Math.max(8, top), minWidth, maxWidth }}
    >
      <div className="grid shrink-0 grid-cols-7 overflow-hidden rounded-sm border border-black/50">
        {preview.pixels.map((pixel, index) => (
          <div
            key={index}
            className={`size-2.5 ${index === 24 ? "ring-1 ring-inset ring-white" : ""}`}
            style={{ backgroundColor: `rgba(${pixel.r}, ${pixel.g}, ${pixel.b}, ${pixel.alpha})` }}
          />
        ))}
      </div>
      <div className="flex min-w-max flex-1 items-center gap-2 overflow-visible">
        <div className="size-3.5 shrink-0 rounded-sm border border-white/20" style={{ backgroundColor: swatch }} />
        <div className="whitespace-nowrap font-mono text-[10px] text-slate-100">{label}</div>
      </div>
    </div>
  );
}

export function FrameNameInput({
  frame,
  onCommit,
  onCancel,
  onWheel,
}: {
  frame: FrameLabelOverlay;
  onCommit: (value: string) => void;
  onCancel: () => void;
  onWheel: (event: React.WheelEvent<Element>) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    input?.focus();
    input?.select();
  }, []);

  return (
    <input
      ref={inputRef}
      className="pointer-events-auto absolute h-5 max-w-56 rounded-sm border border-sky-400 bg-[#1b222a] px-1 text-[11px] font-medium leading-5 text-sky-100 outline-none shadow-lg shadow-black/30"
      style={{
        left: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        transform: `rotate(${frame.rotation}deg)`,
        transformOrigin: "0 0",
      }}
      defaultValue={frame.name}
      onPointerDown={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onWheel={onWheel}
      onBlur={event => {
        if (!cancelledRef.current) {
          onCommit(event.currentTarget.value);
        }
      }}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(event.currentTarget.value);
          return;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          cancelledRef.current = true;
          onCancel();
        }
      }}
      aria-label={`Rename ${frame.name}`}
    />
  );
}
